import React from 'react';
import SurveyItem from '../../../util/SurveyItem';
import Survey from '../../../util/Survey';
import {
  withSurveyContext,
  withIsReadOnly,
  WithSurveyContextInjectedProps,
  WithIsReadOnlyInjectedProps,
} from '../../Contexts';
import SurveyField from '../../SurveyField';
import withCheckForEnablement, {
  WithCheckForEnablementInjectedProps,
} from '../../hoc/withCheckForEnablement';
import withValidation, {
  WithValidationInjectedProps,
} from '../../hoc/withValidation';
import { FieldProps } from 'formik';
import SurveyItemContainer from '../../SurveyItemContainer';
import ChoiceOptions from '../../../util/ChoiceOptions';
import SelectField from '../../InputControls/SelectField';

import {
  WithStyles,
  createStyles,
  withStyles,
} from '@lifeomic/chroma-react/styles';

const styles = () =>
  createStyles({
    root: {
      width: '100%',
      maxWidth: 500,
      // Without this the select keeps its min-content width and pushes past a
      // field-width cap (e.g. out of a repeating-group instance card).
      minWidth: 0,
    },
  });

export interface FieldComponentOwnProps {
  validate: (value: any) => void;
  responseAnswerType: string;
  survey: Survey;
  surveyItem: SurveyItem;
  isReadOnly: boolean;
  error: string;
  shouldShowError: boolean;
  className?: string;
}

export type FieldComponentProps = FieldComponentOwnProps & FieldProps<any>;

class FieldComponent extends React.PureComponent<FieldComponentProps> {
  choiceOptions: ChoiceOptions;

  constructor(props: FieldComponentProps) {
    super(props);

    /*
      Note that the option list is static and won't update with prop updates.
      This should be fine and reduce downstream re-renders and list generation.
      If this causes a problem, build the choices within the render method.
     */
    const { survey, surveyItem } = this.props;

    this.choiceOptions = survey.getChoiceOptionsForItem(surveyItem.link);
  }

  handleChange = (newValue: any) => {
    this.props.form.setFieldValue(this.props.field.name, newValue);
  };

  render() {
    if (this.choiceOptions === null) return null;

    const {
      field: { value },
      surveyItem,
      isReadOnly,
      shouldShowError,
      error,
      className,
    } = this.props;

    return (
      <SelectField
        className={className}
        itemType={this.choiceOptions.selectFieldType}
        value={value}
        onChange={this.handleChange}
        options={this.choiceOptions.values}
        isOpenChoice={surveyItem.type === 'open-choice'}
        isMultiSelect={surveyItem.repeats}
        label={surveyItem.label}
        disabled={isReadOnly}
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        error={shouldShowError ? error : null}
        linkId={surveyItem.link}
        aria-label={`Input for ${surveyItem.text}`}
      />
    );
  }
}

export interface ChoiceOwnProps {
  surveyItem: SurveyItem;
}

export type ChoiceProps = ChoiceOwnProps &
  WithIsReadOnlyInjectedProps &
  WithCheckForEnablementInjectedProps &
  WithValidationInjectedProps &
  WithSurveyContextInjectedProps &
  WithStyles<typeof styles>;

class Choice extends React.PureComponent<ChoiceProps> {
  render() {
    const {
      surveyItem,
      survey,
      isEnabled,
      shouldShowError,
      error,
      validate,
      isReadOnly,
      classes,
    } = this.props;

    if (!isEnabled) return null;

    return (
      <SurveyItemContainer surveyItem={surveyItem}>
        <SurveyField
          className={classes.root}
          validate={validate}
          responseAnswerType={surveyItem.responseValueType}
          name={surveyItem.link}
          component={FieldComponent}
          survey={survey}
          surveyItem={surveyItem}
          isReadOnly={isReadOnly}
          error={error}
          shouldShowError={shouldShowError}
        />
      </SurveyItemContainer>
    );
  }
}

export default withIsReadOnly(
  withCheckForEnablement(
    withValidation(withSurveyContext(withStyles(styles)(Choice))),
  ),
);
